import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Send, LoaderCircle, FileCheck } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { contactInfo, heroImages } from "@/data/content";

const WEB3FORMS_ACCESS_KEY = "3292a7d4-b6cc-4be0-8c79-3ca75e0cb0b9";

const initialForm = { nome: "", email: "", telefono: "", azienda: "", messaggio: "", privacy: false };

const inputClass =
  "w-full rounded-xl border border-navy/15 bg-white px-4 py-3.5 text-sm text-navy placeholder:text-navy/35 outline-none transition-all duration-300 focus:border-gold focus:ring-2 focus:ring-gold/25";

const Contatti = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.privacy) {
      toast.error("Devi accettare la Privacy Policy per inviare il messaggio.");
      return;
    }
    setSending(true);
    try {
      const response = await axios.post("https://api.web3forms.com/submit", {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: "Nuova richiesta dal sito Vobis Credit",
        nome: form.nome,
        email: form.email,
        telefono: form.telefono,
        azienda: form.azienda,
        messaggio: form.messaggio,
      });
      if (response.data.success) {
        toast.success("Messaggio inviato con successo. Ti ricontatteremo entro 24 ore lavorative.");
        setForm(initialForm);
      } else {
        toast.error("Si è verificato un errore nell'invio. Riprova o scrivici via email.");
      }
    } catch (err) {
      toast.error("Si è verificato un errore nell'invio. Riprova o scrivici via email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contatti-page">
      <PageHero
        eyebrow="Contatti"
        lines={["Parliamone", <span key="i" className="italic text-gold">insieme</span>]}
        description="Vuoi valorizzare i tuoi portafogli di crediti deteriorati? Il nostro team è a disposizione per analizzare le tue esigenze e offrire soluzioni su misura."
        image={heroImages.contatti}
      />

      <section className="bg-cream py-28" data-testid="contatti-main-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid gap-14 lg:grid-cols-5">
          {/* FORM */}
          <Reveal className="lg:col-span-3">
            <form
              onSubmit={handleSubmit}
              data-testid="contact-form"
              className="rounded-[2rem] border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12"
            >
              <h2 className="font-display text-3xl text-navy">Richiedi una consulenza</h2>
              <p className="mt-3 text-sm leading-relaxed text-navy/55">
                Compila il form: ti ricontatteremo entro 24 ore lavorative. Tutte le richieste sono trattate con la massima riservatezza.
              </p>

              <div className="mt-9 grid gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="contact-nome" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-navy/60">Nome e Cognome *</label>
                  <input id="contact-nome" data-testid="contact-input-nome" required value={form.nome} onChange={(e) => update("nome", e.target.value)} placeholder="Mario Rossi" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="contact-email" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-navy/60">Email *</label>
                  <input id="contact-email" data-testid="contact-input-email" type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="mario.rossi@esempio.it" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="contact-telefono" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-navy/60">Telefono</label>
                  <input id="contact-telefono" data-testid="contact-input-telefono" type="tel" value={form.telefono} onChange={(e) => update("telefono", e.target.value)} placeholder="+39 333 000 0000" className={inputClass} />
                </div>
                <div>
                  <label htmlFor="contact-azienda" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-navy/60">Azienda / Ente</label>
                  <input id="contact-azienda" data-testid="contact-input-azienda" value={form.azienda} onChange={(e) => update("azienda", e.target.value)} placeholder="Nome dell'organizzazione" className={inputClass} />
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="contact-messaggio" className="mb-2 block text-xs font-semibold uppercase tracking-[0.18em] text-navy/60">Messaggio *</label>
                  <textarea id="contact-messaggio" data-testid="contact-input-messaggio" required rows={5} value={form.messaggio} onChange={(e) => update("messaggio", e.target.value)} placeholder="Descrivici la tua esigenza: tipologia di portafoglio, importi indicativi, tempistiche..." className={`${inputClass} resize-none`} />
                </div>
              </div>

              <label className="mt-7 flex cursor-pointer items-start gap-3 text-sm text-navy/60" data-testid="contact-privacy-label">
                <input
                  type="checkbox"
                  data-testid="contact-input-privacy"
                  checked={form.privacy}
                  onChange={(e) => update("privacy", e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-[#C9962E]"
                />
                <span>
                  Ho letto e accetto la <Link to="/privacy" className="font-semibold text-gold-dark underline underline-offset-2">Privacy Policy</Link> e acconsento al trattamento dei miei dati per essere ricontattato. *
                </span>
              </label>

              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group mt-9 inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 text-sm font-semibold text-navy shadow-xl shadow-gold/25 transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
              >
                {sending ? (
                  <>
                    <LoaderCircle size={17} className="animate-spin" />
                    Invio in corso...
                  </>
                ) : (
                  <>
                    Invia la richiesta
                    <Send size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5" />
                  </>
                )}
              </button>
            </form>
          </Reveal>

          {/* RECAPITI */}
          <div className="space-y-6 lg:col-span-2">
            <Reveal delay={0.1}>
              <div className="rounded-[2rem] bg-navy p-9" data-testid="contact-info-card">
                <h3 className="font-display text-2xl text-white">Recapiti diretti</h3>
                <ul className="mt-7 space-y-6">
                  <li className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-gold"><Mail size={19} /></span>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Email</div>
                      <a href={`mailto:${contactInfo.email}`} data-testid="contact-email-link" className="mt-1 block text-sm text-white/85 transition-colors hover:text-gold">{contactInfo.email}</a>
                      <a href={`mailto:${contactInfo.pec}`} className="mt-0.5 block text-sm text-white/85 transition-colors hover:text-gold">{contactInfo.pec} (PEC)</a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-gold"><Phone size={19} /></span>
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.2em] text-white/45">Telefono</div>
                      <a href={`tel:${contactInfo.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="mt-1 block text-sm text-white/85 transition-colors hover:text-gold">{contactInfo.phone}</a>
                      <span className="mt-0.5 block text-xs text-white/40">Lun – Ven, 9:00 – 18:00</span>
                    </div>
                  </li>
                </ul>
              </div>
            </Reveal>

            {contactInfo.offices.map((o, i) => (
              <Reveal key={o.label} delay={0.16 + i * 0.08}>
                <div data-testid={`office-card-${i}`} className="flex items-start gap-4 rounded-[2rem] border border-navy/10 bg-white p-7 transition-all duration-300 hover:border-gold/50">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-navy text-gold"><MapPin size={19} /></span>
                  <div>
                    <div className="font-display text-lg text-navy">{o.city}</div>
                    <div className="mt-0.5 text-xs font-semibold uppercase tracking-[0.2em] text-gold-dark">{o.label}</div>
                    <div className="mt-1.5 text-sm font-medium text-navy/80">{o.address}</div>
                    <p className="mt-2 text-xs leading-relaxed text-navy/55">{o.description}</p>
                  </div>
                </div>
              </Reveal>
            ))}

          </div>
          </div>

          <Reveal delay={0.1} className="mt-14">
            <div data-testid="riservatezza-band" className="flex flex-col items-center justify-center gap-4 rounded-[2rem] border border-gold/40 bg-gold/10 px-8 py-9 text-center sm:flex-row sm:text-left">
              <FileCheck size={26} className="shrink-0 text-gold-dark" />
              <p className="max-w-3xl text-sm leading-relaxed text-navy/70 md:text-base">
                Ogni richiesta è coperta da <strong className="text-navy">assoluta riservatezza</strong>: i dati dei portafogli proposti non vengono mai condivisi con terzi.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MAPPA */}
      <section className="bg-white py-28" data-testid="mappa-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal className="mb-12 text-center">
            <span className="inline-flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.3em] text-gold">
              <span className="h-px w-8 bg-gold" />Dove Siamo<span className="h-px w-8 bg-gold" />
            </span>
            <h2 className="mt-5 font-display text-4xl text-navy sm:text-5xl">Roma e Milano</h2>
          </Reveal>
          <Reveal>
            <div className="grid gap-6 md:grid-cols-2">
              <div className="overflow-hidden rounded-[2rem] border border-navy/10 bg-white shadow-2xl shadow-navy/10" data-testid="map-card-roma">
                <div className="flex items-center gap-3 border-b border-navy/10 px-7 py-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-gold"><MapPin size={18} /></span>
                  <div>
                    <div className="font-display text-lg text-navy">Roma</div>
                    <div className="text-xs font-medium text-navy/55">Viale dell'Arte 19/a – 00144 Roma</div>
                  </div>
                </div>
                <iframe
                  title="Mappa sede Vobis Credit — Roma"
                  data-testid="contact-map-roma"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=12.4514%2C41.8213%2C12.4914%2C41.8413&layer=mapnik&marker=41.8313%2C12.4714"
                  className="h-[360px] w-full"
                  loading="lazy"
                />
              </div>
              <div className="overflow-hidden rounded-[2rem] border border-navy/10 bg-white shadow-2xl shadow-navy/10" data-testid="map-card-milano">
                <div className="flex items-center gap-3 border-b border-navy/10 px-7 py-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-navy text-gold"><MapPin size={18} /></span>
                  <div>
                    <div className="font-display text-lg text-navy">Milano</div>
                    <div className="text-xs font-medium text-navy/55">Via Lazzaro Papi, 3 – Milano</div>
                  </div>
                </div>
                <iframe
                  title="Mappa sede Vobis Credit — Milano"
                  data-testid="contact-map-milano"
                  src="https://www.openstreetmap.org/export/embed.html?bbox=9.1867%2C45.4661%2C9.2267%2C45.4861&layer=mapnik&marker=45.4761%2C9.2067"
                  className="h-[360px] w-full"
                  loading="lazy"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Contatti;
